import { persistedAtom } from '../utils/persistedAtom';
import * as zod from 'zod';
import { useAtom, useAtomValue } from 'jotai/react';

/*
 * Layout options
 */

const planningLayoutAtom = persistedAtom({
  key: 'planning_layout',
  schema: zod.enum(['horizontal', 'vertical']),
  defaultValue: 'horizontal',
  /** For proper rendering of timeline we need to read value from storage on init */
  getOnInit: true,
});

export const usePlanningLayoutState = () => useAtom(planningLayoutAtom);

/*
 * Timeline voyage box field options
 */

/* Cargo details visibility */
const isVoyageCargoVisibleAtom = persistedAtom({
  schema: zod.boolean(),
  key: 'is_voyage_cargo_visible',
  defaultValue: true,
});

export const useIsVoyageCargoVisibleState = () =>
  useAtom(isVoyageCargoVisibleAtom);

/* Available capacity visibility */
const isVoyageCapacityVisibleAtom = persistedAtom({
  key: 'is_voyage_capacity_visible',
  schema: zod.boolean(),
  defaultValue: false,
});

export const useIsVoyageCapacityVisibleState = () =>
  useAtom(isVoyageCapacityVisibleAtom);

/* Voyage attributes visibility */
const isVoyageAttributeVisibleAtom = persistedAtom({
  key: 'is_voyage_attribute_visible',
  schema: zod.boolean(),
  defaultValue: true,
});

export const useIsVoyageAttributeVisibleState = () =>
  useAtom(isVoyageAttributeVisibleAtom);

/* Charterer visibility */
const isVoyageChartererVisibleAtom = persistedAtom({
  key: 'is_voyage_charterer_visible',
  schema: zod.boolean(),
  defaultValue: false,
});

export const useIsVoyageChartererVisibleState = () =>
  useAtom(isVoyageChartererVisibleAtom);

/* Missing port cost visibility */
const isMissingPortCostVisibleAtom = persistedAtom({
  key: 'is_missing_port_cost_visible',
  schema: zod.boolean(),
  defaultValue: false,
});

export const useIsMissingPortCostVisibleState = () =>
  useAtom(isMissingPortCostVisibleAtom);

const isRegionToolAmountsMTSVisibleAtom = persistedAtom({
  key: 'is_region_tool_amounts_mts_visible',
  schema: zod.boolean(),
  defaultValue: true,
  getOnInit: true,
});
export const useRegionToolAmountsMTSVisibleState = () =>
  useAtom(isRegionToolAmountsMTSVisibleAtom);

const isRegionToolAmountsCBMVisibleAtom = persistedAtom({
  key: 'is_region_tool_amounts_cbm_visible',
  schema: zod.boolean(),
  defaultValue: true,
  getOnInit: true,
});
export const useRegionToolAmountsCBMVisibleState = () =>
  useAtom(isRegionToolAmountsCBMVisibleAtom);

const regionToolIntervalAtom = persistedAtom({
  key: 'region_tool_interval',
  schema: zod.enum(['weeks', 'months']),
  defaultValue: 'months',
});
export const useRegionToolIntervalState = () => useAtom(regionToolIntervalAtom);

const isTCEVisibleAtom = persistedAtom({
  key: 'is_tce_visible',
  schema: zod.boolean(),
  defaultValue: false,
});

export const useVoyageBoxTCEState = () => useAtom(isTCEVisibleAtom);

const isVCPVisibleAtom = persistedAtom({
  key: 'is_vcp_visible',
  schema: zod.boolean(),
  defaultValue: false,
});

export const useVoyageBoxVCPState = () => useAtom(isVCPVisibleAtom);

const isProfitVisibleAtom = persistedAtom({
  key: 'is_profit_visible',
  schema: zod.boolean(),
  defaultValue: false,
});

export const useVoyageBoxProfitState = () => useAtom(isProfitVisibleAtom);

/**
 * Vessel data visibility options
 */

/* Vessel capacity details visibility */
const isVesselCapacityVisibleAtom = persistedAtom({
  schema: zod.boolean(),
  key: 'is_vessel_capacity_visible',
  defaultValue: true,
});

export const useIsVesselCapacityVisibleState = () =>
  useAtom(isVesselCapacityVisibleAtom);

/* Vessel remarks details visibility */
const isVesselRemarksVisibleAtom = persistedAtom({
  key: 'is_vessel_remarks_visible',
  schema: zod.boolean(),
  defaultValue: false,
});

export const useIsVesselRemarksVisibleState = () =>
  useAtom(isVesselRemarksVisibleAtom);

/* Whether to highlight voyages with violations */
const isViolationVoyagesHighLightedAtom = persistedAtom({
  key: 'is_violation_voyages_highlighted',
  schema: zod.boolean(),
  defaultValue: false,
});

export const useIsViolationVoyagesHighlightedState = () =>
  useAtom(isViolationVoyagesHighLightedAtom);

const violationIndicatorBasis = ['eta', 'operation_start', 'none'] as const;

export type ViolationIndicatorBasis = (typeof violationIndicatorBasis)[number];

export const violationIndicatorBasisAtom = persistedAtom({
  key: 'violation_indicator_basis',
  schema: zod.enum(violationIndicatorBasis),
  defaultValue: 'eta',
});

export const useViolationIndicatorBasisState = () =>
  useAtom(violationIndicatorBasisAtom);

/* Whether new assignments wait for laycan by default (otherwise requires holding SHIFT) */
export const isWaitLaycanDefaultAtom = persistedAtom({
  key: 'is_wait_laycan_default',
  schema: zod.boolean(),
  defaultValue: false,
});

export const useIsWaitLaycanDefaultState = () =>
  useAtom(isWaitLaycanDefaultAtom);

export function useViolationBasis() {
  return useAtomValue(violationIndicatorBasisAtom);
}
